import {
  Box,
  CloseIcon,
  Drawer,
  FindAProviderIcon,
  GetHelpIcon,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MessagesIcon,
  NavigationIcon,
  ReadLearnIcon,
  TalkToCarrotIcon,
  YourBenefitGuideIcon,
  YourCarrotPlanIcon,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import { UserDropdown } from "features/global-ui/user-dropdown"
import React, { ReactElement } from "react"
import { useIntl } from "react-intl"
import { Link as RouterLink } from "react-router-dom"
import { FinancialSupportNavItem } from "./FinancialSupportNavItem"
import { FeatureGate } from "utils/FeatureGate"
import { ZendeskLink } from "components/views/atoms/links/ZendeskTextLink"
import { useFlags } from "launchdarkly-react-client-sdk"
import { useHasProviderFinder } from "./hooks/useHasProviderFinder"
import { useCurrentUser } from "components/context/user/UserContext"

export function NavItem({
  link,
  icon,
  text,
  onClick,
  button = false
}: {
  text: string
  icon: ReactElement
  link?: string
  onClick?: () => void
  button?: boolean
}): JSX.Element {
  const linkProps = button ? {} : { component: RouterLink, to: link }
  const active = link === window.location.pathname || (link !== "/" && window.location.pathname.startsWith(link))
  return (
    <Box borderLeft={(theme) => `4px solid ${active ? theme.palette.primary.main : theme.palette.background.default}`}>
      <ListItem disablePadding>
        <ListItemButton
          {...linkProps}
          sx={(theme) => ({ padding: theme.spacing(theme.tokens.spacing.md) })}
          onClick={onClick}
        >
          <ListItemIcon>{icon}</ListItemIcon>
          <ListItemText primary={text} />
        </ListItemButton>
      </ListItem>
    </Box>
  )
}

function NavItemsList({ setDrawerOpen }: { setDrawerOpen: (drawerOpen: boolean) => void }): JSX.Element {
  const intl = useIntl()
  const closeDrawer = () => setDrawerOpen(false)
  const { isInAppLockdown, companyId, company } = useCurrentUser()
  const hasProviderFinder = useHasProviderFinder(company.countryCode, companyId)
  const { enablePhoneSupport } = useFlags()
  const CARROT_PHONE_SUPPORT_FULL_LAUNCH_DATE = new Date("2024-01-01T12:00:00.000Z")
  const now = new Date()

  const navItemsToShow: {
    component: ReactElement
    key: string
    showForAppLockedDown: boolean
    showForCarrotLite: boolean
    isHidden?: boolean
  }[] = [
    {
      key: "nav-messages-link",
      showForAppLockedDown: true,
      showForCarrotLite: true,
      component: (
        <NavItem
          text={intl.formatMessage({ defaultMessage: "Messages" })}
          icon={<MessagesIcon />}
          link={"/messages"}
          onClick={closeDrawer}
        />
      )
    },
    {
      key: "nav-library-link",
      showForAppLockedDown: true,
      showForCarrotLite: true,
      component: (
        <NavItem
          text={intl.formatMessage({ defaultMessage: "Read & learn" })}
          icon={<ReadLearnIcon />}
          link={"/read"}
          onClick={closeDrawer}
        />
      )
    },
    {
      key: "nav-transactions-link",
      showForAppLockedDown: false,
      showForCarrotLite: false,
      component: <FinancialSupportNavItem onClick={closeDrawer} />
    },
    {
      key: "nav-ttc-link",
      showForAppLockedDown: enablePhoneSupport || now > CARROT_PHONE_SUPPORT_FULL_LAUNCH_DATE ? true : false,
      showForCarrotLite: true,
      component: (
        <NavItem
          text={intl.formatMessage({
            defaultMessage: "Talk to Carrot"
          })}
          icon={<TalkToCarrotIcon />}
          link={"/talk-to-carrot"}
          onClick={closeDrawer}
        />
      )
    },
    {
      key: "nav-provider-finder-link",
      showForAppLockedDown: false,
      showForCarrotLite: true,
      isHidden: !hasProviderFinder,
      component: (
        <NavItem
          text={intl.formatMessage({ defaultMessage: "Find a provider" })}
          icon={<FindAProviderIcon />}
          link={"/providerfinder"}
          onClick={closeDrawer}
        />
      )
    },
    {
      key: "nav-your-benefit-guide-link",
      showForAppLockedDown: true,
      showForCarrotLite: true,
      component: (
        <NavItem
          text={intl.formatMessage({ defaultMessage: "Your benefit guide" })}
          icon={<YourBenefitGuideIcon />}
          link={"/your-benefit-guide"}
          onClick={closeDrawer}
        />
      )
    },
    {
      key: "nav-support-center-link",
      showForAppLockedDown: true,
      showForCarrotLite: true,
      component: (
        <FeatureGate filter={(c) => c.isSupportCenterEnabled}>
          <ZendeskLink underline="none">
            <NavItem text={intl.formatMessage({ defaultMessage: "Get help" })} icon={<GetHelpIcon />} button />
          </ZendeskLink>
        </FeatureGate>
      )
    }
  ]
    .filter((ni) => (isInAppLockdown && ni.showForAppLockedDown) || !isInAppLockdown)
    .filter((ni) => (company.carrotLite && ni.showForCarrotLite) || !company.carrotLite)
    .filter(({ isHidden }) => !isHidden)

  return (
    <List>
      <Box marginY={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
        <NavItem
          text={intl.formatMessage({ defaultMessage: "Your Carrot Plan" })}
          icon={<YourCarrotPlanIcon />}
          link={"/"}
          onClick={closeDrawer}
        />
      </Box>
      {navItemsToShow.map(({ component, key }) => React.cloneElement(component, { key }))}
    </List>
  )
}

export function MainNavigation(): JSX.Element {
  /** This component is a stub of what will be the new main navigation it's hidden behind a dark release toggle for now */
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false)
  const theme = useTheme()
  const showMobileNav = useMediaQuery(theme.breakpoints.down("md"))
  const intl = useIntl()
  return (
    <>
      {showMobileNav ? (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          padding={(theme) => theme.tokens.spacing.md}
          bgcolor={(theme) => theme.palette.background.default}
        >
          <IconButton
            onClick={() => {
              setDrawerOpen(true)
            }}
          >
            <NavigationIcon />
          </IconButton>
          <Link href="/" underline="none">
            <Box
              component="img"
              src={`/images/carrot-logo.png`}
              aria-label={intl.formatMessage({ defaultMessage: "Go to homepage" })}
            />
          </Link>
          <UserDropdown />
        </Box>
      ) : null}
      <Drawer
        anchor="left"
        variant={showMobileNav ? "temporary" : "permanent"}
        PaperProps={{
          sx: (theme) => ({
            width: "256px",
            background: theme.palette.background.default,
            // zIndex added to ensure this drawer always appears behind the dynamic content tool
            zIndex: 19
          })
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        {showMobileNav ? (
          <Box
            display="flex"
            justifyContent="flex-end"
            paddingTop={(theme) => theme.spacing(theme.tokens.spacing.md)}
            paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.sm)}
            paddingX={(theme) => theme.spacing(theme.tokens.spacing.md)}
          >
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "close" })}
              onClick={() => setDrawerOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            marginTop={(theme) => theme.tokens.spacing.xxxl}
            marginLeft={(theme) => theme.tokens.spacing.lg}
            width="130px"
          >
            <Link href="/" underline="none">
              <Box
                component="img"
                src={`/images/carrot-logo.png`}
                aria-label={intl.formatMessage({ defaultMessage: "Go to homepage" })}
              />
            </Link>
          </Box>
        )}

        <NavItemsList {...{ setDrawerOpen }} />
      </Drawer>
    </>
  )
}
